import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { imgUrl } from "@/lib/api";

const ROTATIONS = ["-1.5deg", "1deg", "-0.75deg", "1.5deg"];
const LIGHT_COLORS = ["Natural", "White", "Blossom", "Bay", "Butter", "Seafoam", "White/Pink", "White/Black"];
const COLOR_SWATCHES = {
  Black: "#111111", Natural: "#E8E2D4", Ash: "#B9B9B4", White: "#FAFAF7",
  "Heather Grey": "#B9B9B4", Grey: "#B9B9B4",
  Blossom: "#EFC9D4", Bay: "#B4C7D6", Butter: "#F8E08E", Seafoam: "#C4D7CE",
};

export default function ProductCard({ product, index = 0, className = "", colorOverride = "" }) {
  const [imgStage, setImgStage] = useState(0);
  const [pickedColor, setPickedColor] = useState("");
  const rotate = ROTATIONS[index % ROTATIONS.length];
  const activeColor = pickedColor || colorOverride || product.card_color || product.color;
  const posterBg =
    activeColor === "Natural"
      ? "bg-[#E8E2D4] text-black"
      : LIGHT_COLORS.includes(activeColor)
        ? "bg-[#FAFAF7] text-black"
        : "bg-[#111] text-[#F4F4F0]";
  // Model-first: the approved on-body photo for the CARD's color leads whenever it exists.
  // No drawn-flat fallback: on load failure we step down to the product photo, then the slogan poster.
  const modelForCard = product.model_images?.[activeColor]
    || (product.model_image && (!product.model_image_color || product.model_image_color === activeColor) ? product.model_image : null);
  const src = imgStage === 0
    ? modelForCard || product.image
    : imgStage === 1
      ? product.image
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group ${className}`}
      style={{ transform: `rotate(${rotate})` }}
    >
      <Link to={`/product/${product.slug}?color=${encodeURIComponent(activeColor)}`} data-testid={`product-card-${product.slug}`} className="block">
        <div className="relative aspect-[4/5] border-2 border-black overflow-hidden shadow-hard bg-[#111] group/img">
          <div className={`absolute inset-0 flex items-center justify-center p-8 ${posterBg}`}>
            <span className="font-display uppercase text-center text-3xl sm:text-4xl leading-[0.95] tracking-tight">
              {product.slogan}
            </span>
          </div>
          {src && (
            <img
              src={imgUrl(src)}
              alt={`${product.name} — ${activeColor}`}
              onError={() => setImgStage((s) => s + 1)}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105"
            />
          )}
          {product.back_image && (
            <img
              src={imgUrl(product.back_image)}
              alt={`${product.name} — back print`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-top opacity-0 transition-opacity duration-300 group-hover/img:opacity-100"
            />
          )}
          <span className="absolute bottom-3 left-3 border-2 border-black bg-accent px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.12em] whitespace-nowrap text-black">
            {product.type === "hoodie" ? "Heavyweight Hoodie" : product.type === "baby-tee" ? "Fitted Baby Tee" : product.type === "womens-tee" ? "Women's Relaxed" : "Unisex Heavyweight"}</span>
          {product.back_image && (
            <span className="absolute bottom-3 left-3 border-2 border-black bg-primary px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white" data-testid="front-back-badge">
              Front + Back Print
            </span>
          )}
        </div>
        <div className="mt-4 flex items-start justify-between gap-3 px-1">
          <div>
            <p className="font-display uppercase text-xl leading-none decoration-primary decoration-[3px] underline-offset-4 group-hover:underline">{product.name}</p>
            <div className="mt-1.5 flex items-center gap-2">
              {(colorOverride || product.card_color || pickedColor) && (
                <span className="border-2 border-black bg-background px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-black" data-testid={`card-color-${product.slug}`}>
                  {activeColor}
                </span>
              )}
              {product.type === "womens-tee" ? (
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Women's</p>
              ) : product.type !== "baby-tee" && (
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Unisex</p>
              )}
            </div>
            {(product.colors || []).length > 1 && (
              <div className="mt-2 flex items-center gap-1.5" data-testid={`card-swatches-${product.slug}`}>
                {(product.colors || []).map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`card-swatch-${product.slug}-${c.toLowerCase()}`}
                    aria-label={`${product.name} in ${c}`}
                    aria-pressed={activeColor === c}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPickedColor(c);
                      setImgStage(0);
                    }}
                    className={`h-5 w-5 rounded-sm border-2 transition-all ${
                      activeColor === c ? "border-black ring-2 ring-primary" : "border-black/40 hover:border-black"
                    }`}
                    style={{ background: COLOR_SWATCHES[c] || "#999" }}
                  />
                ))}
              </div>
            )}
            <p className="mt-1 font-mono text-xs uppercase tracking-wider text-primary font-bold">{product.slogan}</p>
          </div>
          <p className="font-mono text-sm font-bold whitespace-nowrap text-primary">${product.price.toFixed(2)}</p>
        </div>
      </Link>
    </motion.div>
  );
}
